/**
 * Access Code Card Component
 *
 * Displays an existing access code with status and usage info.
 * Note: Email/SMS sending is only available immediately after generating a new code
 * because the plaintext code is not stored for security reasons.
 */

import { Clock, Ban, Mail, Phone, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useRevokeAccessCode } from '../hooks/useInstallationJobs';
import type { AccessCode } from '../types';

interface AccessCodeCardProps {
  code: AccessCode;
  jobId: string;
}

export function AccessCodeCard({ code, jobId }: AccessCodeCardProps) {
  const revokeCode = useRevokeAccessCode();

  const isExpired = new Date(code.expiresAt) < new Date();
  const isRevoked = !!code.revokedAt;
  const isActive = !isExpired && !isRevoked;
  const wasSent = code.sentViaEmail || code.sentViaSms;

  const handleRevoke = async () => {
    if (!confirm('Are you sure you want to revoke this access code?')) return;

    try {
      await revokeCode.mutateAsync({ jobId, codeHash: code.codeHash });
      toast.success('Access code revoked');
    } catch (error) {
      toast.error('Failed to revoke access code');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-CA');
  };

  const getExpiryText = () => {
    const expiresAt = new Date(code.expiresAt);
    const now = new Date();
    const diffMs = expiresAt.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Expires today';
    if (diffDays === 1) return 'Expires tomorrow';
    return `Expires in ${diffDays} days`;
  };

  return (
    <div className={`p-4 rounded-lg border ${isActive ? 'border-border' : 'border-muted bg-muted/50'}`}>
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
              {code.codeHash.substring(0, 8)}...
            </code>
            {isRevoked ? (
              <Badge variant="destructive">Revoked</Badge>
            ) : isExpired ? (
              <Badge variant="secondary">Expired</Badge>
            ) : (
              <Badge variant="success">Active</Badge>
            )}
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {getExpiryText()}
            </span>
            <span>Used {code.useCount} time{code.useCount !== 1 ? 's' : ''}</span>
          </div>
          {code.lastUsedAt && (
            <p className="text-xs text-muted-foreground">
              Last used: {formatDate(code.lastUsedAt)}
            </p>
          )}
          {wasSent && (
            <div className="flex items-center gap-2 mt-2">
              {code.sentViaEmail && (
                <Badge variant="outline" className="text-xs">
                  <Mail className="h-3 w-3 mr-1" />
                  Email sent
                </Badge>
              )}
              {code.sentViaSms && (
                <Badge variant="outline" className="text-xs">
                  <Phone className="h-3 w-3 mr-1" />
                  SMS sent
                </Badge>
              )}
              {code.sentTo && (
                <span className="text-xs text-muted-foreground">to {code.sentTo}</span>
              )}
            </div>
          )}
          {!wasSent && isActive && (
            <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
              <AlertCircle className="h-3 w-3" />
              <span>To send via email, generate a new code</span>
            </div>
          )}
        </div>
        {isActive && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRevoke}
              disabled={revokeCode.isPending}
            >
              <Ban className="h-4 w-4 mr-1" />
              Revoke
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
